// Word Art preset registry - the single source of truth for the Word Art
// style gallery AND the live on-garment preview. A Word Art "style" is NOT
// a Font row: it is a heavy display face plus a warp (slant), plus a 3D
// extrude or hard-shadow layer. On screen it renders as solid SVG; when
// stitched it maps to discrete Isacord thread colours (the launch set is
// deliberately gradient-free, so screen == stitch).
//
// Launch = the three FAITHFUL presets only. Gradient presets (rainbow arch,
// chrome slant, purple fade, sunset wave, rainbow slab) are DEFERRED until
// the digitiser signs off on the thread-band approximations - add them here
// with a fill spec + a "thread-approximated" / "needs-review" fidelity.

/** Template tag that identifies the dedicated Word Art template. A matched
 *  template carrying this tag turns the customiser's font picker into the
 *  Word Art style gallery. Deliberately tag-driven (the same way every other
 *  template binds) so lighting Word Art up needs no schema migration and no
 *  config-resolver change. Per-preset curation can later ride
 *  Config.wordArtStyles instead. */
export const WORD_ART_TEMPLATE_TAG = "rk-word-art";

export type WordArtWarp = "none" | "slant-up";

export type WordArtFidelity =
  | "faithful"
  | "thread-approximated"
  | "needs-review";

/** How the SECOND colour (the 3D side / hard shadow) is derived. The face
 *  colour is always the customer's picked thread; the second colour either
 *  darkens that pick (one thread choice drives both) or is a fixed hue. */
export type WordArtSecondary =
  | { mode: "darken"; factor: number }
  | { mode: "fixed"; hex: string };

export type WordArtPreset = {
  /** Stable slug. Rides the cart line as _spot_N_wordart_style_id. */
  id: string;
  /** Customer-facing gallery label. */
  name: string;
  /** CSS face for BOTH the gallery tile and the live preview. The digitiser
   *  punches the matching Wilcom lettering per embroideryRef. */
  fontFamily: string;
  /** Wilcom lettering / effect reference for the back office. */
  embroideryRef: string;
  /** Caps-only look (classic Word Art). Upper-cases input + preview. */
  forceUppercase?: boolean;
  /** Baseline warp. "slant-up" tilts the word up to the right. */
  warp: WordArtWarp;
  /** Degrees for the slant-up warp (negative = up to the right). */
  slantDeg?: number;
  /** 3D extrude behind the face. dx/dy are fractions of fontMm per step;
   *  `depth` stepped copies build the extruded side. Null = no extrude. */
  extrude?: { dx: number; dy: number; depth: number } | null;
  /** Hard offset drop-shadow (single layer). Fractions of fontMm. Null =
   *  no shadow. */
  shadow?: { dx: number; dy: number } | null;
  /** Second-colour source for the extrude / shadow layer. */
  secondary: WordArtSecondary;
  /** Representative colour for the gallery tile before a thread is picked. */
  sampleHex: string;
  /** How faithfully it embroiders vs the on-screen look. */
  fidelity: WordArtFidelity;
  /** Number of thread colours the stitched result uses. */
  threadCount: number;
  /** Customer-facing note about the stitched result. */
  stitchNote: string;
};

export const WORD_ART_PRESETS: WordArtPreset[] = [
  {
    id: "cool-dad-3d",
    name: "Cool Dad 3D",
    fontFamily:
      "'Archivo Black', Impact, 'Arial Black', system-ui, sans-serif",
    embroideryRef: "wilcom:block-3d-extrude",
    forceUppercase: true,
    warp: "none",
    extrude: { dx: 0.05, dy: 0.07, depth: 6 },
    shadow: null,
    secondary: { mode: "darken", factor: 0.5 },
    sampleHex: "#39a7ff",
    fidelity: "faithful",
    threadCount: 2,
    stitchNote:
      "Stitches in 2 thread colours: your colour on the face, a darker shade for the 3D side.",
  },
  {
    id: "classic-shadow",
    name: "Classic shadow",
    fontFamily:
      "'Archivo Black', Impact, 'Arial Black', system-ui, sans-serif",
    embroideryRef: "wilcom:block-hard-shadow",
    forceUppercase: true,
    warp: "none",
    extrude: null,
    shadow: { dx: 0.1, dy: 0.12 },
    secondary: { mode: "darken", factor: 0.42 },
    sampleHex: "#b3b3b3",
    fidelity: "faithful",
    threadCount: 2,
    stitchNote:
      "Stitches in 2 thread colours: your colour with a hard shadow behind.",
  },
  {
    id: "slant-up-3d",
    name: "Slant up",
    fontFamily:
      "'Archivo Black', Impact, 'Arial Black', system-ui, sans-serif",
    embroideryRef: "wilcom:slant-up-extrude",
    forceUppercase: true,
    warp: "slant-up",
    slantDeg: -8,
    extrude: { dx: 0.045, dy: 0.06, depth: 5 },
    shadow: null,
    secondary: { mode: "darken", factor: 0.5 },
    sampleHex: "#1f7ae0",
    fidelity: "faithful",
    threadCount: 2,
    stitchNote:
      "Stitches in 2 thread colours: your colour on the face, a darker shade for the 3D side.",
  },
];

export function wordArtPresetById(
  id: string | null | undefined,
): WordArtPreset | null {
  if (!id) return null;
  return WORD_ART_PRESETS.find((p) => p.id === id) ?? null;
}

/** Darken a #rrggbb hex toward black by `factor` (0..1). Returns the input
 *  unchanged if it is not a 6-digit hex. */
export function shadeHex(hex: string, factor: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const f = Math.max(0, Math.min(1, factor));
  const ch = (shift: number) => Math.round(((n >> shift) & 255) * f);
  const to2 = (v: number) => v.toString(16).padStart(2, "0");
  return `#${to2(ch(16))}${to2(ch(8))}${to2(ch(0))}`;
}

/** Face + second (extrude/shadow) colour for a preset given the customer's
 *  picked thread hex. */
export function resolveWordArtColours(
  preset: WordArtPreset,
  primaryHex: string,
): { face: string; second: string } {
  const second =
    preset.secondary.mode === "fixed"
      ? preset.secondary.hex
      : shadeHex(primaryHex, preset.secondary.factor);
  return { face: primaryHex, second };
}
